import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { resolveMaxFill } from '../../constants/aid';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { AlertRow } from '../shell/AlertRow';
import { Spinner } from '../Spinner';
import { NumericInput } from '../strategy/NumericInput';
import type { AidShellProps } from '../../pages/Aid';
import type { ResourceAmounts } from '../../types/aid';

const RESOURCE_LABELS: Record<keyof ResourceAmounts, string> = {
  gold: 'Gold',
  food: 'Food',
  soldiers: 'Soldiers',
  runes: 'Runes',
};

const RESOURCE_ORDER: (keyof ResourceAmounts)[] = ['gold', 'food', 'soldiers', 'runes'];

/**
 * SF-154: mobile card-based content for Aid — 3 cards (Your Resources / Recipient / Aid
 * Package). Commit's preview summary/disabled-reason/submit button folded into the end of "Aid
 * Package" (Phase 3 Component 2, matching the Thievery "Operation" card precedent) — no separate
 * 4th "Commit" card and no sticky bottom bar.
 */
export function MobileAidShell({
  province,
  availableResources,
  resourceAmounts,
  handleResourceChange,
  handleMaxFill,
  handleClearAll,
  resourceResetKey,
  selectedTarget,
  setSelectedTarget,
  kingdomMembers,
  isLoadingKingdom,
  kingdomLoadError,
  loadKingdom,
  tradeValue,
  maxShipment,
  estimatedTaxInfo,
  isOverLimit,
  isApproachingLimit,
  canSubmit,
  submitDisabledReason,
  isSubmitting,
  handleSendAid,
  aidResult,
}: AidShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  // SF-154 Decision 9 / SF-149 precedent: post-submit result reachability — imperative focus move
  // on the result container when it transitions from null to set.
  useEffect(() => {
    if (aidResult) {
      resultRef.current?.focus();
    }
  }, [aidResult]);

  const tradeBalance = province.resources.tradeBalance;

  return (
    <div className="space-y-3">
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold text-game-50 outline-none">
        Aid
      </h1>

      {aidResult && (
        <div
          ref={resultRef}
          tabIndex={-1}
          className="outline-none"
          style={{ scrollMarginTop: 60 }}
          role={aidResult.success ? 'status' : 'alert'}
        >
          {aidResult.success ? (
            <div
              className="rounded p-3 space-y-2 text-sm"
              style={{ backgroundColor: COLORS.successBg, border: `1px solid ${COLORS.successBorder}` }}
            >
              <p className="font-semibold" style={{ color: COLORS.successText }}>{aidResult.message}</p>
              <div className="grid grid-cols-2 gap-2" style={{ color: COLORS.text.secondary }}>
                <div>Shipment Value: {formatNumber(Math.round(aidResult.shipmentValue))}gc</div>
                <div>Tax Applied: {(aidResult.taxRate * 100).toFixed(1)}%</div>
              </div>
              {aidResult.taxRate > 0 && (
                <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
                  Recipient received: {formatNumber(aidResult.receivedGold)}gc,{' '}
                  {formatNumber(aidResult.receivedFood)} food,{' '}
                  {formatNumber(aidResult.receivedSoldiers)} soldiers,{' '}
                  {formatNumber(aidResult.receivedRunes)} runes
                </div>
              )}
              <div className="pt-2" style={{ borderTop: `1px solid ${COLORS.successBorder}`, color: COLORS.text.secondary }}>
                Your new trade balance:{' '}
                <span style={{ color: aidResult.senderTradeBalance >= 0 ? COLORS.positive : COLORS.negative }}>
                  {aidResult.senderTradeBalance >= 0 ? '+' : ''}
                  {formatNumber(Math.round(aidResult.senderTradeBalance))}gc
                </span>
              </div>
            </div>
          ) : (
            <div className="text-sm" style={{ color: COLORS.negative }}>{aidResult.message}</div>
          )}
        </div>
      )}

      <GlanceCard title="Your Resources">
        <MetricRow label="Gold" value={formatNumber(availableResources.gold)} unit="gc" weight="primary" />
        <MetricRow label="Food" value={formatNumber(availableResources.food)} unit="bushels" weight="secondary" />
        <MetricRow label="Soldiers" value={formatNumber(availableResources.soldiers)} unit="" weight="secondary" />
        <MetricRow label="Runes" value={formatNumber(availableResources.runes)} unit="runes" weight="secondary" />
        <MetricRow
          label="Trade Balance"
          value={`${tradeBalance >= 0 ? '+' : ''}${formatNumber(Math.round(tradeBalance))}`}
          unit="gc"
          weight="tertiary"
          valueColor={tradeBalance >= 0 ? COLORS.positive : COLORS.negative}
        />
      </GlanceCard>

      <GlanceCard title="Recipient">
        <div className="flex items-center gap-2">
          {isLoadingKingdom ? (
            <div className="flex items-center gap-2">
              <Spinner size="sm" />
              <span className="text-sm" style={{ color: COLORS.text.tertiary }}>Loading...</span>
            </div>
          ) : (
            <select
              aria-label="Recipient"
              value={selectedTarget}
              onChange={(e) => setSelectedTarget(e.target.value)}
              className="flex-1 rounded text-sm py-2 px-2"
              style={{ backgroundColor: COLORS.inputBg, border: `1px solid ${COLORS.inputBorder}`, color: COLORS.text.primary, minHeight: 44 }}
            >
              <option value="">Select a province...</option>
              {kingdomMembers.map((member) => (
                <option key={member.provinceId} value={member.provinceId}>
                  {member.provinceName} ({member.location})
                </option>
              ))}
            </select>
          )}
        </div>

        {kingdomLoadError ? (
          <>
            <AlertRow severity="critical" message={kingdomLoadError} />
            <button
              onClick={() => loadKingdom()}
              className="text-sm px-2.5 rounded-lg self-start"
              style={{ minHeight: 44, color: COLORS.link, border: `1px solid ${COLORS.link}`, background: 'none' }}
            >
              Retry
            </button>
          </>
        ) : !isLoadingKingdom && kingdomMembers.length === 0 ? (
          <AlertRow severity="warning" message="Your kingdom has no other members to send aid to." />
        ) : null}
      </GlanceCard>

      <GlanceCard
        title="Aid Package"
        headerAction={
          <button
            onClick={handleClearAll}
            aria-label="Clear all resource amounts"
            className="text-xs"
            style={{ color: COLORS.link, background: 'none', minHeight: 44 }}
          >
            Clear all
          </button>
        }
      >
        {RESOURCE_ORDER.map((resource) => {
          const label = RESOURCE_LABELS[resource];
          const available = availableResources[resource];
          const maxFill = resolveMaxFill(resource, resourceAmounts, available, maxShipment);
          return (
            <div key={resource} className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className="text-sm w-20 flex-shrink-0" style={{ color: COLORS.text.secondary }}>{label}</span>
                <NumericInput
                  value={resourceAmounts[resource]}
                  onChange={(v) => handleResourceChange(resource, v)}
                  min={0}
                  max={available}
                  ariaLabel={`${label} amount`}
                  resetKey={resourceResetKey}
                  size="touch"
                  className="flex-1"
                />
                <button
                  onClick={() => handleMaxFill(resource)}
                  disabled={maxFill === 0}
                  aria-label={`Max ${label}`}
                  className="text-xs px-2.5 rounded-lg disabled:opacity-50"
                  style={{ minHeight: 44, color: COLORS.link, border: `1px solid ${COLORS.link}`, background: 'none' }}
                >
                  Max
                </button>
              </div>
              <div className="text-[10px]" style={{ color: COLORS.text.muted }}>
                / {formatNumber(available)} available
              </div>
            </div>
          );
        })}

        {isOverLimit ? (
          <AlertRow
            severity="warning"
            message={`Shipment exceeds maximum allowed (${formatNumber(maxShipment)}gc). Reduce resources.`}
          />
        ) : isApproachingLimit ? (
          <AlertRow severity="warning" message="Approaching shipment limit (90% of max)" />
        ) : null}

        <div className="flex flex-col gap-1 text-sm">
          <div className="flex items-center justify-between">
            <span style={{ color: COLORS.text.tertiary }}>Trade Value</span>
            <span className="font-mono" style={{ color: isOverLimit ? COLORS.negative : COLORS.text.primary }}>
              {formatNumber(Math.round(tradeValue))}gc
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span style={{ color: COLORS.text.tertiary }}>Max Shipment</span>
            <span className="font-mono" style={{ color: COLORS.text.primary }}>
              ~{formatNumber(maxShipment)}gc
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span style={{ color: COLORS.text.tertiary }}>Estimated Tax</span>
            <span className="font-mono" style={{ color: estimatedTaxInfo && estimatedTaxInfo.taxRate > 0 ? COLORS.warning : COLORS.text.primary }}>
              {selectedTarget && estimatedTaxInfo ? `${(estimatedTaxInfo.taxRate * 100).toFixed(1)}%` : '—'}
            </span>
          </div>
        </div>
        <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
          {selectedTarget && estimatedTaxInfo
            ? `Recipient receives ~${formatNumber(estimatedTaxInfo.receivedGold)}gc, ${formatNumber(estimatedTaxInfo.receivedFood)} food, ${formatNumber(estimatedTaxInfo.receivedSoldiers)} soldiers, ${formatNumber(estimatedTaxInfo.receivedRunes)} runes`
            : '—'}
        </div>

        <div className="text-[10px]" style={{ color: COLORS.text.tertiary }}>
          Values: Gold=1gc, Food=0.05gc, Soldiers=100gc, Runes=3gc
        </div>

        {submitDisabledReason && (
          <div className="text-xs" style={{ color: COLORS.warning }}>
            {submitDisabledReason}
          </div>
        )}

        <button
          onClick={handleSendAid}
          disabled={!canSubmit}
          className="w-full text-center py-2.5 rounded-lg text-sm font-bold disabled:opacity-50"
          style={{
            minHeight: 44,
            backgroundColor: COLORS.btnPrimary,
            border: `1px solid ${COLORS.btnPrimaryHover}`,
            color: COLORS.text.primary,
          }}
        >
          {isSubmitting ? 'Sending…' : 'Send Aid'}
        </button>
      </GlanceCard>
    </div>
  );
}
